/**
 * OS data reader/writer. Reads a client's real quotes/SOs/POs from the shared
 * MFX-OS Firestore, and creates real OS draft quotes the OS editor can open
 * natively via its /editor/<id> route (core.js boot restore). READ + the single
 * quote-draft WRITE only.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where, limit, orderBy, addDoc, setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { newDraft, type SalesOrder } from '@mfx/contracts';
import type { Account } from './accounts';
import { ordersFor, quotesFor, posFor, isDemoQuery, type Order, type Quote, type IncomingPO } from './clientDeepData';

const num = (...vals: any[]): number => { for (const v of vals) { const n = Number(v); if (!Number.isNaN(n) && n) return n; } return 0; };
const str = (...vals: any[]): string => { for (const v of vals) { if (v != null && String(v).trim()) return String(v).trim(); } return ''; };

function mapQuote(id: string, d: any): Quote {
  const status = String(d.status || '').toLowerCase();
  const st: Quote['status'] = status.includes('accept') || status.includes('won') ? 'negotiating'
    : status.includes('sent') ? 'sent' : status.includes('view') ? 'viewed' : status.includes('draft') ? 'draft' : 'sent';
  return {
    id: str(d.quoteNum, d.quoteNumber, d.qNum, id),
    product: str(d.sku, (Array.isArray(d.skus) && d.skus[0]), (d.fields && d.fields.jobDesc), 'Quote'),
    qty: str(d.poSelectedQty, d.poQty, (Array.isArray(d.qtys) && d.qtys[0]), '—'),
    value: num(d.totalAmount, d.poSelectedTotal, d.poTotal, d.setupTotal, d.total),
    status: st,
    age: str(d.followUpDate, d.updatedAt, 'recent'),
    osId: id,
  };
}

function mapSO(id: string, d: Partial<SalesOrder>): Order {
  const status = String(d.status || '').toLowerCase();
  const st: Order['status'] = status.includes('ship') ? 'shipped' : status.includes('ready') ? 'ready'
    : status.includes('production') || status.includes('progress') ? 'in_production' : 'confirmed';
  return {
    id: str(d.soNum, id),
    po: str(d.poNumber, '—'),
    product: str((Array.isArray(d.skus) && d.skus[0]), 'Sales order'),
    qty: str(d.selectedQty, d.skuCount ? `${d.skuCount} SKU` : '', '—'),
    value: num(d.total),
    status: st,
    due: str(d.estimatedShipDate, d.poRequiredDate, '—'),
    soId: id,
    email: str((d as any).email, (d as any).poClientEmail, (d as any).customerEmail, (d as any).fields && (d as any).fields.custEmail),
    rawStatus: status,
  };
}

function mapIncomingPO(id: string, d: Partial<SalesOrder>): IncomingPO | null {
  const poNumber = str(d.poNumber);
  if (!poNumber) return null;
  const status = String(d.status || '').toLowerCase();
  return {
    id, po: poNumber,
    received: str(d.updatedAt, d.createdAt, 'recent'),
    value: num(d.total),
    ship: str(d.poRequiredDate, 'Net 30'),
    status: status.includes('confirm') || status.includes('production') || status.includes('ship') ? 'accepted' : 'reviewing',
  };
}

async function tryQuery(name: string, field: string, value: string) {
  try {
    const snap = await getDocs(query(collection(db, name), where(field, '==', value), limit(100)));
    return snap.docs;
  } catch { return []; }
}

export interface Deals { orders: Order[]; quotes: Quote[]; pos: IncomingPO[]; live: boolean; }

/** Real-but-empty deals: returned when there's no Firestore match and we're not in demo. */
const EMPTY_DEALS: Deals = { orders: [], quotes: [], pos: [], live: false };

export async function loadClientDeals(account: Account): Promise<Deals | null> {
  const osCustomerId = account.id.replace(/^cust_/, '').replace(/^vend_/, '');
  const byName = account.name;
  const qDocs = [
    ...await tryQuery('quotes', 'customerId', osCustomerId),
    ...await tryQuery('quotes', 'customerName', byName),
    ...await tryQuery('quotes', 'customer', byName),
    ...await tryQuery('quotes', 'accountName', byName),
  ];
  const soDocs = [
    ...await tryQuery('salesOrders', 'customerId', osCustomerId),
    ...await tryQuery('salesOrders', 'customerName', byName),
    ...await tryQuery('salesOrders', 'customer', byName),
    ...await tryQuery('salesOrders', 'accountName', byName),
    ...await tryQuery('salesOrders', 'company', byName),
    ...await tryQuery('salesOrders', 'companyName', byName),
  ];

  const seen = new Set<string>();
  const quotes = qDocs.filter((x) => !seen.has(x.id) && seen.add(x.id)).map((x) => mapQuote(x.id, x.data()));
  const seen2 = new Set<string>();
  const soData = soDocs.filter((x) => !seen2.has(x.id) && seen2.add(x.id));
  const orders = soData.map((x) => mapSO(x.id, x.data()));
  const pos = soData.map((x) => mapIncomingPO(x.id, x.data())).filter(Boolean) as IncomingPO[];

  if (quotes.length || orders.length || pos.length) return { quotes, orders, pos, live: true };
  return null;
}

export function useClientDeals(account: Account | undefined): Deals {
  const id = account?.id ?? 'cust_unknown';
  const demo = isDemoQuery();
  // Synthetic deals are demo-only. Outside demo we start empty and fill from Firestore.
  const synth = useMemo<Deals>(
    () => (demo ? { orders: ordersFor(id), quotes: quotesFor(id), pos: posFor(id), live: false } : EMPTY_DEALS),
    [id, demo],
  );
  const [deals, setDeals] = useState<Deals>(synth);

  useEffect(() => {
    setDeals(synth);
    if (demo || !account) return;
    let alive = true;
    loadClientDeals(account)
      .then((r) => { if (alive) setDeals(r ?? EMPTY_DEALS); }) // null match => empty, not synthetic
      .catch(() => {});
    return () => { alive = false; };
  }, [id, demo]); // eslint-disable-line react-hooks/exhaustive-deps

  return deals;
}


// ── OS Quote Engine read/write ────────────────────────────────
export interface RecentQuote { id: string; quoteNum: string; customerName: string; status: string; total: number; followUpDate: string; sku: string; }

export async function loadRecentQuotes(n = 50): Promise<RecentQuote[]> {
  const map = (d: any, id: string): RecentQuote => ({
    id, quoteNum: str(d.quoteNum, d.quoteNumber, d.qNum, id),
    customerName: str(d.customerName, d.customer, (d.fields && d.fields.custCo), '—'),
    status: str(d.status, 'draft').toLowerCase(),
    total: num(d.totalAmount, d.poSelectedTotal, d.poTotal, Array.isArray(d.qtys) && d.qtys[0] && d.qtys[0].total),
    followUpDate: str(d.followUpDate, ''),
    sku: str(d.sku, (Array.isArray(d.skus) && d.skus[0]), ''),
  });
  try {
    const snap = await getDocs(query(collection(db, 'quotes'), orderBy('updatedAt', 'desc'), limit(n)));
    return snap.docs.map((x) => map(x.data(), x.id));
  } catch {
    try { const snap = await getDocs(query(collection(db, 'quotes'), limit(n))); return snap.docs.map((x) => map(x.data(), x.id)); }
    catch { return []; }
  }
}

export interface QtyTier { qty: number; ppu: number; setup: number; total: number; }
export interface OSQuoteInput {
  quoteNum: string; customerName: string; customerId?: string; sku: string;
  status: string; qtys: QtyTier[]; selectedQtyIndex: number; totalAmount: number;
  validityDays: number; terms: string; followUpDate?: string;
  poNumber?: string; poRequiredDate?: string; poClientEmail?: string;
  clientFolderLink?: string; driveFileId?: string;
}

export async function saveQuoteToOS(q: OSQuoteInput): Promise<{ ok: boolean; id?: string; error?: string }> {
  try {
    const ref = await addDoc(collection(db, 'quotes'), {
      ...q, source: 'mfx-crm', createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    return { ok: true, id: ref.id };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'write failed' };
  }
}


// ── OS deep links via the OS's NATIVE /editor/<id> route ──────
// core.js boot restore reads location.pathname; `editor/<id>` => openEditor(id).
// No URL params, no hook, no race — the OS does it itself after auth.
export const OS_BASE = ((import.meta as any).env?.VITE_OS_BASE_URL as string) || 'https://os.microflexfilm.com';

/** Direct link the OS opens natively into a specific quote's editor. */
export function osEditorUrl(quoteId: string): string {
  return `${OS_BASE}/editor/${encodeURIComponent(quoteId)}`;
}

/** Legacy: kept for any old callers. Prefer osEditorUrl / createOSDraft. */
export function osQuoteEngineUrl(opts: { quoteId?: string; customer?: string; customerId?: string } = {}): string {
  if (opts.quoteId) return osEditorUrl(opts.quoteId);
  return `${OS_BASE}/`;
}

const osUid = () => 'q' + Date.now() + Math.random().toString(36).slice(2, 6);
function genQN(): string {
  const d = new Date(), yy = String(d.getFullYear()).slice(2), mm = String(d.getMonth() + 1).padStart(2, '0');
  const r = Math.floor(Math.random() * 9000) + 1000;
  return 'MF' + yy + mm + '-T' + r;
}

/** Create a real OS draft quote (valid shape) and return its id so the caller
 *  can open ${OS_BASE}/editor/<id>. The OS opens it natively on load. */
export async function createOSDraft(opts: { customerName?: string; customerId?: string; estimator?: string } = {}): Promise<{ ok: boolean; id?: string; error?: string }> {
  const id = osUid();
  const now = new Date().toISOString();
  // Canonical OS draft shape from @mfx/contracts, plus CRM-specific provenance.
  const q = newDraft({
    id,
    quoteNum: genQN(),
    now,
    estimator: opts.estimator || '',
    createdBy: opts.estimator || 'MFX CRM',
    customerId: opts.customerId || null,
    customerName: opts.customerName || '',
  });
  q.fields.custCo = opts.customerName || '';
  q.source = 'mfx-crm';
  try {
    await setDoc(doc(db, 'quotes', id), q);
    return { ok: true, id };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'write failed' };
  }
}
